export interface MonthData {
  name: string;
  title: string;
  skills: string[];
  kpis: [string, string][];
  warnings: string[];
  triggers: string[];
  quote: string;
}

export interface Phase {
  num: string;
  name: string;
  sub: string;
  colorCls: string;
  goal: string;
  status: 'done' | 'active' | 'locked';
  months: MonthData[];
}

export const phases: Phase[] = [
  {
    num: 'T01–02', name: 'Sinh tồn', sub: 'Survival', colorCls: 'phase-color-1',
    goal: 'Không cháy tài khoản, làm quen với rủi ro, hiểu bản thân sâu hơn',
    status: 'done',
    months: [
      {
        name: 'Tháng 1', title: 'Củng cố kỷ luật giao dịch',
        skills: ['Kỷ luật cá nhân', 'Xác nhận điều kiện', 'Giao dịch theo rule', 'Giới hạn số lệnh'],
        kpis: [['Lệnh đúng hệ thống', '≥ 90%'], ['Không phá SL/TP', '100%'], ['FOMO/revenge', '≤ 2/tháng'], ['Tổng lệnh', '15–25 lệnh']],
        warnings: ['Ảo tưởng kiểm soát khi mới thắng vài tháng', 'Lấy lý do biện minh cho lệnh cảm tính', 'Giao dịch khi tâm trạng xấu, thiếu ngủ', "'Tốt bụng với lệnh thua': không đóng khi đã sai rõ"],
        triggers: ['Thua 3 lệnh liên tiếp → nghỉ 24–48h', 'Vi phạm checklist 3 lần/tuần → đọc lại SOP', 'Cảm giác căng thẳng khi nhìn chart → dừng 1 phiên'],
        quote: 'Kỷ luật không phải là ép buộc — đó là cam kết với chính tương lai của bạn.',
      },
      {
        name: 'Tháng 2', title: 'Trading Journal chuyên sâu',
        skills: ['Tự phản hồi sau lệnh', 'Nhận diện mô hình cá nhân', 'Viết bài học thực chiến', 'Hệ thống hóa qua phân tích'],
        kpis: [['% lệnh có journal', '≥ 90%'], ['Bài học trích xuất', '≥ 8/tháng'], ['Mô hình lỗi nhận diện', '≥ 2 lỗi'], ['Phản hồi tâm lý', '≥ 80%']],
        warnings: ["Biến journal thành 'ghi cho có' — mất giá trị phản hồi", 'Ghi xong nhưng không rút kết luận hoặc cải tiến', 'Lười ghi sau lệnh thua — dẫn đến lặp lại sai lầm'],
        triggers: ['Lặp lại cùng 1 lỗi trong 3 lệnh → reset journal', 'Không ghi 5 lệnh gần nhất → pause 1 phiên', 'Ghi kiểu cảm tính, thiếu logic → nghỉ 24h làm lại'],
        quote: 'Nếu bạn không thể học từ chính lệnh của mình, thị trường sẽ nhắc lại bài học đó — với giá đắt hơn.',
      },
    ],
  },
  {
    num: 'T03–04', name: 'Ổn định', sub: 'Stabilization', colorCls: 'phase-color-2',
    goal: 'Có lợi nhuận nhỏ, đều đặn, bắt đầu có hệ thống rõ ràng',
    status: 'active',
    months: [
      {
        name: 'Tháng 3', title: 'Ổn định hệ thống giao dịch',
        skills: ['Chuẩn hóa SOP', 'Backtest dữ liệu thực', 'Tối ưu R:R & Winrate', 'Hệ thống hóa mindset'],
        kpis: [['Backtest', '≥ 200 lệnh'], ['Expectancy', '> 0.3R/lệnh'], ['Profit Factor', '≥ 1.5'], ['Tuân đúng SOP', '≥ 90%']],
        warnings: ["Dùng quá nhiều setup → không hệ thống hóa được", "Thấy 'có vẻ đúng' nên vào lệnh — không rõ tiêu chuẩn", 'Sửa hệ thống mỗi tuần → không đủ dữ liệu kiểm chứng'],
        triggers: ['5 lệnh liên tiếp sai hệ thống → phải xem lại SOP', 'Sửa SOP quá 2 lần/tuần → cần dừng để ổn định', 'Lệnh thắng mà không biết tại sao thắng → không được vào tiếp'],
        quote: 'Một hệ thống tốt không thắng ở mỗi lệnh, mà thắng qua chuỗi dài.',
      },
      {
        name: 'Tháng 4', title: 'Tối ưu quản lý vốn',
        skills: ['Position Sizing', 'Kiểm soát rủi ro tổng thể', 'Kỹ năng scale vốn', 'Tư duy vốn bảo thủ'],
        kpis: [['Risk/lệnh', '0.25–0.5% vốn'], ['SL tối đa/tuần', '≤ 2.5–3%'], ['Tính đúng khối lượng', '≥ 95%'], ['Drawdown tháng', '≤ 6%']],
        warnings: ['Overconfidence sau chuỗi thắng → tăng lot không kiểm soát', "Giao dịch khối lượng lớn để 'gỡ lại nhanh'", 'Coi thường 1 lệnh nhỏ → mất cảnh giác với kỷ luật'],
        triggers: ['Drawdown vượt 6% → nghỉ 3 ngày, review toàn bộ', 'Giao dịch 2 lệnh size sai → khóa tài khoản 24h', 'Tăng lot bất thường sau thắng lớn → reset plan'],
        quote: 'Trade như một quỹ đầu tư — chậm mà chắc, sống sót để thống trị.',
      },
    ],
  },
  {
    num: 'T05–06', name: 'Hệ thống hóa', sub: 'Systemization', colorCls: 'phase-color-3',
    goal: 'Giao dịch theo quy trình như một hệ thống chuyên nghiệp',
    status: 'locked',
    months: [
      {
        name: 'Tháng 5', title: 'Tăng hiệu suất giao dịch',
        skills: ['Tối ưu R:R', 'Tối ưu điểm entry', 'Sàng lọc setup mạnh', 'Tối ưu chiến lược thoát'],
        kpis: [['R trung bình/lệnh', '≥ 0.8R'], ['Profit Factor', '≥ 1.8'], ['Winrate', '≥ 45%'], ['Lệnh ngoài setup A', '≤ 2/tháng']],
        warnings: ["Trade nhiều lệnh để 'gỡ' → giảm R trung bình", 'Không kiên nhẫn chờ điểm vào → SL dày, R giảm', 'Chốt lời sớm vì sợ mất → R thực tế thấp'],
        triggers: ['R trung bình tuần < 0.5 → nghỉ, phân tích 3 lệnh', '3 lệnh liên tiếp out sớm → review chiến lược exit'],
        quote: 'Tinh hoa không nằm ở việc trade nhiều — mà ở việc chỉ trade khi thật sự đáng.',
      },
      {
        name: 'Tháng 6', title: 'Ổn định tâm lý',
        skills: ['Nhận diện cảm xúc', 'Trạng thái trung tính', 'Cơ chế dừng đúng lúc', 'Chống FOMO/Revenge'],
        kpis: [['% lệnh log cảm xúc', '≥ 90%'], ['Lệnh FOMO/revenge', '≤ 2/tháng'], ['Phiên lệch chuẩn', '≤ 3 phiên'], ['Nghỉ đúng lúc', '≥ 2 lần']],
        warnings: ['Không ghi lại cảm xúc → dần mất kiểm soát mà không biết', 'Cố gồng tâm lý → dẫn đến burnout', 'Thắng lớn → hưng phấn quá mức, vào lệnh liều'],
        triggers: ['3 lệnh liên tiếp sai hệ thống → nghỉ 24h', 'Tâm lý hoảng loạn, vào lệnh liên tục → dừng cả tuần', 'Tự thấy không kiểm soát được bản thân → tắt máy ngay'],
        quote: 'Để thành công dài hạn, bạn cần EQ còn hơn cả IQ phân tích.',
      },
    ],
  },
  {
    num: 'T07–08', name: 'Tối ưu', sub: 'Optimization', colorCls: 'phase-color-4',
    goal: 'Tăng hiệu suất, giảm rủi ro, tối đa hóa lợi thế cạnh tranh',
    status: 'locked',
    months: [
      {
        name: 'Tháng 7', title: 'Scale vốn an toàn',
        skills: ['Scaling có kế hoạch', 'Kiểm soát cảm xúc scale', 'Bảo toàn trước drawdown', 'Tư duy trung lập với PnL'],
        kpis: [['Tăng khối lượng/chu kỳ', '≤ 30%'], ['Drawdown khi scale', '≤ 4%'], ['Lệnh bị ảnh hưởng tâm lý', '≤ 10%'], ['Lần phải scale xuống', '≤ 1']],
        warnings: ['Tăng size quá nhanh → tâm lý không theo kịp', 'Dính chuỗi thua ngay khi mới tăng size → chấn tâm lý', "Né entry vì 'tiếc nếu thua lớn'"],
        triggers: ['Drawdown ≥ 5% sau scale → giảm size, tạm nghỉ', 'Từ chối entry 2 lần vì sợ thua → scale về mức cũ'],
        quote: 'Lợi nhuận lớn chỉ đến khi bạn đủ bản lĩnh chịu được thua lỗ lớn — mà không mất chính mình.',
      },
      {
        name: 'Tháng 8', title: 'Mô hình hóa hệ thống cá nhân',
        skills: ['SOP tổng thể', 'Mô hình hóa setup', 'Sơ đồ quyết định lệnh', 'Dashboard chuẩn hóa'],
        kpis: [['Setup viết lại hoàn chỉnh', '≥ 2 mô hình'], ['Checklist SOP', '3 checklist'], ['Dashboard cá nhân', '≥ 1 bảng'], ['Flowchart quyết định', '≥ 1 sơ đồ']],
        warnings: ["Làm hệ thống để 'trông chuyên nghiệp' nhưng không vận hành", 'Copy hệ thống người khác nhưng không hiểu bản chất'],
        triggers: ['Vẫn mơ hồ khi chọn lệnh → hệ thống chưa đủ rõ', 'Mất phương hướng nếu thiếu cảm xúc → hệ thống chưa thay được cảm tính'],
        quote: 'Một hệ thống tốt không cần bạn giỏi — chỉ cần bạn chịu tuân thủ.',
      },
    ],
  },
  {
    num: 'T09–10', name: 'Mở rộng', sub: 'Scaling Up', colorCls: 'phase-color-5',
    goal: 'Scale vốn, hướng đến cấp vốn ngoài và đầu tư cá nhân dài hạn',
    status: 'locked',
    months: [
      {
        name: 'Tháng 9', title: 'Mindset quỹ & doanh nghiệp',
        skills: ['Tư duy dài hạn quý/năm', 'Báo cáo quản trị', 'Kiểm soát chi phí', 'Ra quyết định như CEO'],
        kpis: [['Báo cáo PnL chuẩn', '≥ 4 tuần'], ['R trung bình/tuần', '≥ 3R'], ['Quyết định CEO style', '≥ 5 lần/tháng'], ['Bản Vision 12 tháng', '1 bản đầy đủ']],
        warnings: ["Vẫn nghĩ như 'game thủ biểu đồ', không phải nhà đầu tư", 'Không kiểm soát chi phí giao dịch ẩn (spread, swap...)'],
        triggers: ['Thua lỗ liên tiếp không biết tại sao → audit tài khoản', 'Chi phí chiếm >20% tổng lãi → tối ưu spread/sàn'],
        quote: 'Đừng là người giỏi bấm nút — hãy là người giỏi kiểm soát toàn bộ hệ thống sinh lời.',
      },
      {
        name: 'Tháng 10', title: 'Tối ưu công nghệ & tự động hóa',
        skills: ['Công cụ chuyên sâu', 'Tự động hóa tác vụ', 'Dashboard tương tác', 'Tối ưu tốc độ phản hồi'],
        kpis: [['Tool áp dụng thực tế', '≥ 3 tool'], ['Tác vụ tự động hóa', '≥ 60%'], ['Giảm thời gian xử lý', '-30% vs trước'], ['Tool ngăn sai sót', '≥ 5 lần']],
        warnings: ['Dùng tool nhưng không hiểu → lệ thuộc máy móc', 'Bị loạn tool → mỗi thứ dùng một ít → không tối ưu thật'],
        triggers: ['Lệch setup vì tín hiệu tool sai → test lại hệ thống cảnh báo', 'Dashboard không đúng thực tế → chỉnh source dữ liệu'],
        quote: 'Trader chuyên nghiệp không làm nhiều hơn — họ làm ít hơn nhưng chính xác hơn.',
      },
    ],
  },
  {
    num: 'T11–12', name: 'Tinh hoa', sub: 'Elite', colorCls: 'phase-color-6',
    goal: 'Vận hành như một quỹ cá nhân, kiểm soát toàn diện, ảnh hưởng cộng đồng',
    status: 'locked',
    months: [
      {
        name: 'Tháng 11', title: 'Tư duy đầu tư & định vị bản thân',
        skills: ['Tư duy tích sản', 'Xây thương hiệu trader', 'Đa dạng hóa tư duy', 'Tăng trưởng có trách nhiệm'],
        kpis: [['Kế hoạch phân bổ lợi nhuận', '1 bản cụ thể'], ['Tài sản ngoài trading', '≥ 5–10% lợi nhuận'], ['Hồ sơ trader hoàn chỉnh', '1 bản'], ['Chiến lược tích sản dài hạn', '≥ 1 chiến lược']],
        warnings: ['Lập brand khi chưa đủ năng lực thực → dễ mất uy tín', 'Lạm dụng vốn đầu tư ngoài chuyên môn'],
        triggers: ['Tiêu hết lợi nhuận không để lại tài sản → xem lại chiến lược', 'Xây cộng đồng nhưng nền tảng chưa vững → củng cố trước'],
        quote: 'Kiếm tiền không khó — giữ được tiền, làm cho tiền sinh thêm giá trị mới khó.',
      },
      {
        name: 'Tháng 12', title: 'Đánh giá toàn diện & Trader 2.0',
        skills: ['Tổng kiểm kê hệ thống', 'Tái cấu trúc mindset', 'Đánh giá công cụ', 'Chuẩn bị ELITE+'],
        kpis: [['Báo cáo tổng kết năm', '1 bản hoàn chỉnh'], ['Phiên bản Trader 2.0', '1 bản chuyên sâu'], ['SOP tối ưu', '1 bản cập nhật'], ['Kế hoạch 6–12 tháng tiếp', '1 bản ELITE']],
        warnings: ['Không đánh giá lại → tiếp tục sai lặp năm cũ', 'Vội chạy theo mục tiêu lớn nhưng chưa hoàn thiện nền tảng'],
        triggers: ["Kết thúc năm không rõ 'tôi đã trưởng thành ra sao' → viết lại hành trình", 'Chưa dám xác nhận ELITE → kiểm tra lại checklist thực tế'],
        quote: 'Khi bạn biết chính xác mình là ai — bạn không cần so sánh, không cần chứng minh.',
      },
    ],
  },
];

export const evalItems = [
  { title: 'Hệ thống có expectancy dương', sub: 'Đã test thực tế > 200 lệnh, có dữ liệu rõ ràng' },
  { title: 'Tuân thủ quy tắc giao dịch > 90%', sub: 'Không vào lệnh ngoài hệ thống, không phá SL/TP' },
  { title: 'Drawdown kiểm soát tốt (< 10%)', sub: 'Lệnh lỗ không gây thiệt hại lớn, hồi phục được' },
  { title: 'Risk mỗi lệnh ổn định (≤ 0.5%)', sub: 'Không overtrade, có tính toán khối lượng chính xác' },
  { title: 'Có nhật ký đầy đủ & phân tích từng lệnh', sub: 'Ghi setup, tâm lý, sai lầm, cải tiến sau mỗi lệnh' },
  { title: 'Winrate và Profit Factor ổn định > 3 tháng', sub: 'Không có chuỗi thua dài bất thường, hiệu suất đều' },
  { title: 'Tâm lý vững khi thua hoặc thắng liên tục', sub: 'Không FOMO, không revenge trade, không loạn nhịp' },
  { title: 'Đã scale vốn ít nhất 1 lần an toàn', sub: 'Giao dịch > 5k hoặc vốn ngoài, tâm lý không bị vỡ' },
  { title: 'Không phụ thuộc vào 1 setup duy nhất', sub: 'Hệ thống thích nghi được nhiều giai đoạn thị trường' },
  { title: 'Có kế hoạch nâng cấp mỗi quý', sub: 'Luôn có mục tiêu phát triển kỹ năng / vốn / mindset' },
];

export const kpiPerformanceRows: [string, string, string, string][] = [
  ['Profit Factor',       '>1.8',    'green', 'HIỆU SUẤT'],
  ['Winrate',             '40–60%',  'green', 'HIỆU SUẤT'],
  ['Expectancy / lệnh',  '>0.5R',   'green', 'HIỆU SUẤT'],
  ['Average R:R',         '≥1:2',    'green', 'HIỆU SUẤT'],
  ['Monthly Return',      '2–5%',    'green', 'HIỆU SUẤT'],
  ['Sharpe Ratio',        '>1.5',    'green', 'HIỆU SUẤT'],
];

export const kpiConsistencyRows: [string, string, string, string][] = [
  ['Lợi nhuận dương',     '≥8/12 tháng', 'gold', 'NHẤT QUÁN'],
  ['Tuần có lợi nhuận',   '>50%',        'gold', 'NHẤT QUÁN'],
  ['Tuân thủ rule',       '≥90%',        'gold', 'NHẤT QUÁN'],
];

export const kpiRiskRows: [string, string, string, string][] = [
  ['Risk per trade',         '<1% vốn',   'red', 'RỦI RO'],
  ['Max Drawdown',           '<10%',      'red', 'RỦI RO'],
  ['Consecutive losses',     '<5 lệnh',   'red', 'RỦI RO'],
  ['% vốn thua / tháng',    '<4%',       'red', 'RỦI RO'],
];

export const kpiDisciplineRows: [string, string, string, string][] = [
  ['FOMO / revenge trade', '≈0',     'gold', 'KỶ LUẬT'],
  ['Vào lệnh đúng lúc',   '≥85%',   'gold', 'KỶ LUẬT'],
  ['Journal đầy đủ',      '100%',   'gold', 'KỶ LUẬT'],
  ['Sửa lỗi từ tuần trước','≥80%',  'gold', 'KỶ LUẬT'],
];

export const kpiBigCards = [
  { label: 'PROFIT FACTOR',   val: '>1.8',  target: 'Elite: >2.0'              },
  { label: 'EXPECTANCY',      val: '>0.5R', target: 'Mỗi lệnh trung bình'      },
  { label: 'MAX DRAWDOWN',    val: '<10%',  target: 'Giới hạn tuyệt đối'       },
  { label: 'MONTHLY RETURN',  val: '2–5%',  target: 'Ổn định, không "ăn may"' },
];

export const elitePlusStages = [
  {
    period: 'Tháng 1–2', title: 'Chuẩn hóa vận hành', colorCls: 'phase-color-1',
    items: ['SOP toàn diện: review, scale, drawdown', 'Dashboard giám sát real-time', 'Tối ưu semi-auto tools'],
  },
  {
    period: 'Tháng 3–4', title: 'Scale vốn có kiểm soát', colorCls: 'phase-color-2',
    items: ['Funded Challenge (FTMO, 5ers...)', 'Pool vốn gia đình / bạn thân', 'Profit Factor ≥1.5 · DD <10%'],
  },
  {
    period: 'Tháng 5–6', title: 'Chu trình Trader 3.0', colorCls: 'phase-color-3',
    items: ['Đánh giá toàn bộ hệ thống mỗi quý', 'Tái cấu trúc tư duy mỗi chu kỳ', 'Học mô hình hành vi thị trường mới'],
  },
  {
    period: 'Tháng 7–8', title: 'Thương hiệu cá nhân', colorCls: 'phase-color-4',
    items: ['Viết trading playbook cá nhân', 'Tham gia cộng đồng tinh hoa', 'Blog / ebook / coaching thử nghiệm'],
  },
  {
    period: 'Tháng 9–10', title: 'Quản lý tài sản bền vững', colorCls: 'phase-color-5',
    items: ['Tài khoản đầu tư vĩnh viễn', 'PAMM / quỹ nội bộ cá nhân', 'Tái đầu tư 100% lợi nhuận 5 năm'],
  },
  {
    period: 'Tháng 11–12', title: 'Tái thiết Trader 4.0', colorCls: 'phase-color-6',
    items: ['Mở cộng đồng / lab giao dịch', 'Kết hợp AI vào hệ thống', 'Bắt đầu chu kỳ nâng cấp tiếp theo'],
  },
];

export const phaseTimeline = [
  { id: 'G.1', name: 'Survival',  status: 'done'   },
  { id: 'G.2', name: 'Stability',   status: 'active' },
  { id: 'G.3', name: 'Systematic',  status: 'locked' },
  { id: 'G.4', name: 'Optimization',    status: 'locked' },
  { id: 'G.5', name: 'Expanding',   status: 'locked' },
  { id: 'G.6', name: 'Elite',  status: 'locked' },
];

export const weeklyR = [
  { week: 'T1', r: 1.2,  positive: true  },
  { week: 'T2', r: -0.4, positive: false },
  { week: 'T3', r: 2.1,  positive: true  },
  { week: 'T4', r: 0.8,  positive: true  },
  { week: 'T5', r: 1.5,  positive: true  },
  { week: 'T6', r: -0.2, positive: false },
  { week: 'T7', r: 1.9,  positive: true  },
  { week: 'T8', r: 2.4,  positive: true  },
];

export const activities = [
  { color: '#4CAF80', text: 'Hoàn thành backtest 200 lệnh Setup A',         time: '2h trước'     },
  { color: '#C9A84C', text: 'Journal tuần 3 — Lỗi: Exit sớm trước TP',      time: 'Hôm qua'      },
  { color: '#5B9BD5', text: 'SOP cập nhật: Thêm điều kiện volume xác nhận', time: '2 ngày trước' },
  { color: '#EF5350', text: 'Cảnh báo: Lệnh FOMO phiên sáng — nghỉ 24h',   time: '3 ngày trước' },
  { color: '#4CAF80', text: 'Profit Factor tuần đạt 1.8 — tiến độ tốt',     time: '1 tuần trước' },
];

export const monthlyKpi = [
  { label: 'Lệnh đúng SOP',  val: '92% ✓', color: '#4CAF80' },
  { label: 'Không phá SL',   val: '100% ✓', color: '#4CAF80' },
  { label: 'FOMO / revenge', val: '1 lệnh', color: '#C9A84C' },
  { label: 'Risk / lệnh',    val: '0.41%',  color: '#4CAF80' },
  { label: 'Drawdown',       val: '4.2%',   color: '#E07048' },
];

export type DayStatus = 'done' | 'partial' | 'missed' | 'future' | 'today';
